import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, X, Link2 } from 'lucide-react';

/**
 * Calendar (month-grid) view for the unified My Tasks page.
 *
 * Renders a 6×7 month grid. Each cell shows up to 3 task chips colour-coded
 * by source (CRM = indigo, Kanban = amber) and overdue (red). Click any day
 * to open the side-sheet modal listing all tasks for that day. Drag a chip
 * onto another day to reschedule.
 *
 * Props:
 *   tasks            flat array of unified tasks (id, description, reminder_date, source, owner, assigned_to, linked_entity_label, priority)
 *   onComplete       (task) => void
 *   onDelete         (task) => void
 *   onOpenParent     (task) => void
 *   onReschedule     (task, newIsoDate) => Promise
 */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function chipClass(t, today) {
  const due = t.reminder_date ? new Date(t.reminder_date) : null;
  if (due && due < today) return 'bg-red-100 text-red-800 border-red-200';
  if (t.source === 'kanban') return 'bg-amber-50 text-amber-900 border-amber-200';
  return 'bg-indigo-50 text-[#3b4872] border-indigo-200';
}

export default function TaskCalendarView({ tasks, onComplete, onDelete, onOpenParent, onReschedule }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [openDayKey, setOpenDayKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragTaskId, setDragTaskId] = useState(null);

  const { gridDays, byDay } = useMemo(() => {
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay()); // back to Sunday
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    const map = {};
    for (const t of tasks) {
      if (!t.reminder_date) continue;
      const d = new Date(t.reminder_date);
      if (Number.isNaN(d.getTime())) continue;
      const k = ymd(d);
      (map[k] ||= []).push(t);
    }
    // sort each day's tasks by datetime ascending
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.reminder_date) - new Date(b.reminder_date)));
    return { gridDays: days, byDay: map };
  }, [cursor, tasks]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date());

  const openTasks = openDayKey ? (byDay[openDayKey] || []) : [];

  const handleDragStart = (e, t) => {
    setDragTaskId(t.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', t.id);
  };

  const handleDragOver = (e, k) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverKey !== k) setDragOverKey(k);
  };

  const handleDrop = async (e, targetKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragTaskId;
    setDragOverKey(null);
    setDragTaskId(null);
    if (!id) return;
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    const oldDate = task.reminder_date ? new Date(task.reminder_date) : new Date();
    const target = new Date(targetKey);
    target.setHours(oldDate.getHours() || 9, oldDate.getMinutes() || 0, 0, 0);
    const oldKey = task.reminder_date ? ymd(new Date(task.reminder_date)) : null;
    if (oldKey === targetKey) return; // same day → no-op
    await onReschedule?.(task, target.toISOString());
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="task-calendar">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <div className="font-display font-bold text-base min-w-[180px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <button onClick={goNext} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-next"><ChevronRight size={16} /></button>
          <button onClick={goToday} className="ml-2 text-[11px] font-display font-semibold uppercase tracking-wider px-2 py-1 border border-slate-300 rounded hover:border-[#3b4872]" data-testid="cal-today">Today</button>
        </div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-200 border border-indigo-300" /> CRM</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300" /> Project</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300" /> Overdue</span>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-slate-100">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500 border-r border-slate-100 last:border-r-0">{w}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 grid-rows-6">
        {gridDays.map((d) => {
          const k = ymd(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = isSameDay(d, today);
          const dayTasks = byDay[k] || [];
          const shown = dayTasks.slice(0, 3);
          const extra = dayTasks.length - shown.length;
          return (
            <div
              key={k}
              onDragOver={(e) => handleDragOver(e, k)}
              onDragLeave={() => dragOverKey === k && setDragOverKey(null)}
              onDrop={(e) => handleDrop(e, k)}
              className={`relative min-h-[110px] border-r border-b border-slate-100 last:border-r-0 transition ${
                inMonth ? 'bg-white' : 'bg-slate-50/50'
              } ${dragOverKey === k ? 'ring-2 ring-[#3b4872] ring-inset bg-indigo-50/40' : ''}`}
              data-testid={`cal-day-${k}`}
            >
              <button
                type="button"
                onClick={() => dayTasks.length && setOpenDayKey(k)}
                className={`absolute inset-0 text-left p-1.5 ${dayTasks.length ? 'hover:bg-slate-50/50 cursor-pointer' : 'cursor-default'}`}
                aria-label={`Open ${k}`}
              />
              <div className="relative p-1.5 pointer-events-none">
                <div className="flex items-center justify-between mb-1">
                  <span className={`inline-flex items-center justify-center text-xs font-display font-bold w-6 h-6 rounded-full ${
                    isToday ? 'bg-[#3b4872] text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'
                  }`}>
                    {d.getDate()}
                  </span>
                  {dayTasks.length > 0 && (
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-400">{dayTasks.length}</span>
                  )}
                </div>
                <div className="space-y-1">
                  {shown.map((t) => (
                    <div
                      key={t.id}
                      draggable={!!onReschedule}
                      onDragStart={(e) => { e.stopPropagation(); handleDragStart(e, t); }}
                      onDragEnd={() => { setDragOverKey(null); setDragTaskId(null); }}
                      className={`text-[10px] truncate px-1.5 py-0.5 border rounded-sm pointer-events-auto ${chipClass(t, today)} ${onReschedule ? 'cursor-grab active:cursor-grabbing' : ''} ${dragTaskId === t.id ? 'opacity-40' : ''}`}
                      title={`${t.description}${onReschedule ? ' — drag to reschedule' : ''}`}
                      data-testid={`cal-chip-${t.id}`}
                    >
                      {t.reminder_date && (
                        <span className="font-display font-semibold tracking-wider mr-1">
                          {new Date(t.reminder_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </span>
                      )}
                      {t.description}
                    </div>
                  ))}
                  {extra > 0 && (
                    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-[#3b4872]">+{extra} more</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Day Detail Side-Sheet */}
      {openDayKey && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={() => setOpenDayKey(null)} data-testid="cal-day-sheet">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">{openTasks.length} task{openTasks.length === 1 ? '' : 's'}</div>
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><CalendarDays size={16} className="text-[#3b4872]" /> {new Date(openDayKey).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
              </div>
              <button onClick={() => setOpenDayKey(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2">
              {openTasks.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No tasks on this day.</p>
              ) : openTasks.map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-sm p-3 hover:bg-slate-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800">{t.description}</div>
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-3">
                        <span>{new Date(t.reminder_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                        {t.owner && <span title="Owner">· {t.owner}</span>}
                        {t.assigned_to && <span className="text-[#3b4872]">→ {t.assigned_to}</span>}
                        <span className={`text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${t.source === 'kanban' ? 'bg-amber-50 text-amber-800' : 'bg-indigo-50 text-[#3b4872]'}`}>
                          {t.source === 'kanban' ? 'Project' : 'CRM'}
                        </span>
                      </div>
                      {(t.linked_entity_label || t.entity_label) && (
                        <button onClick={() => onOpenParent?.(t)} className="mt-1 text-[11px] text-[#3b4872] hover:underline flex items-center gap-1">
                          <Link2 size={11} /> {(t.linked_entity_type || t.entity_type)}: {t.linked_entity_label || t.entity_label}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button onClick={() => onComplete?.(t)} className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-1 border border-slate-300 rounded hover:border-emerald-500 hover:text-emerald-600" data-testid={`cal-complete-${t.id}`}>Done</button>
                      <button onClick={() => onDelete?.(t)} className="text-[10px] font-display font-bold uppercase tracking-wider text-red-600 hover:text-red-700">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
